import { Injectable } from '@angular/core';
import { NavItem, navigation } from '../../app-navigation';

/**
 * Embedded mode: the console running inside another web app's iframe — today
 * the lnwshop admin, through the userscripts in lnwshop_tampermokey_scripts.
 * The host opts in by adding `embeded=true` to the URL it frames:
 *
 *   https://console.genlogic.co.th/#/secure/billings/quote-generator?embeded=true
 *
 * The side menu is replaced by a dropdown of the pages marked `showInEmbeded`
 * in app-navigation.ts (see SecurePageComponent).
 *
 * The `embeded/*` routes are a different thing: single-purpose widgets with no
 * app chrome at all, framed directly. They need no flag.
 */
export const EMBEDED_QUERY_PARAM = 'embeded';

/**
 * Whether `href` asks for embedded mode. Accepts the flag in the router's
 * query (inside the hash, as above) and in the real query string before the
 * hash — the latter is what a host gets from `url.searchParams.set(...)` on
 * the console URL, so both are equally easy to write.
 */
export function isEmbededUrl(href: string): boolean {
    let url: URL;
    try {
        url = new URL(href);
    } catch {
        return false;
    }
    const hashQuery = url.hash.includes('?')
        ? url.hash.slice(url.hash.indexOf('?') + 1)
        : '';
    return [url.searchParams, new URLSearchParams(hashQuery)].some(
        (params) => params.get(EMBEDED_QUERY_PARAM) === 'true',
    );
}

export type EmbededPage = {
    text: string;
    // Router path without the leading slash.
    path: string;
};

/** The `showInEmbeded` items of a navigation tree, in menu order. */
export function embededPages(items: NavItem[]): EmbededPage[] {
    return items.flatMap((item) => [
        ...(item.showInEmbeded && item.path
            ? // The side menu prefixes top-level paths with '/' in place.
              [{ text: item.text, path: item.path.replace(/^\/+/, '') }]
            : []),
        ...embededPages(item.items ?? []),
    ]);
}

// Read once, when the bundle loads — before the router's first navigation.
// It cannot be re-read from the router later: the auth guard's bounce to
// /login, the login form's redirect and ordinary in-app navigation all drop
// query params, and the embed would fall back to the full layout mid-session.
// The document is the unit: a host that reloads the frame reloads its src,
// which carries the flag again.
const EMBEDED_AT_LOAD = isEmbededUrl(window.location.href);

@Injectable({ providedIn: 'root' })
export class EmbededModeService {
    readonly active = EMBEDED_AT_LOAD;
    readonly pages = embededPages(navigation);
}
